import { useLocation, useNavigate } from "react-router-dom";
import MainLayout from "../layout/MainLayout";
import { useEffect, useState } from "react";
import Loader from "../components/Loader";
import { qrLookup } from "../lib/api";

const normalizeRecord = (r) => {
  if (!r || typeof r !== "object") return r;
  return {
    ...r,
    kodKawasan: r.kodKawasan ?? r.kod_kawasan,
    nombor_unit: r.nombor_unit ?? r.nomborUnit,
    nombor_harta: r.nombor_harta ?? r.nomborHarta,
    nombor_akaun: r.nombor_akaun ?? r.nomborAkaun ?? r.no_akaun,
    nama_penyewa: r.nama_penyewa ?? r.namaPenyewa,
  };
};

const formatCurrency = (value) => {
  const n = Number(value ?? 0);
  if (!Number.isFinite(n)) return "RM 0.00";
  return new Intl.NumberFormat("en-MY", {
    style: "currency",
    currency: "MYR",
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(n);
};

const formatPhone = (value) => {
  const phone = String(value ?? "").trim();
  if (!phone || phone === "-") return "-";
  if (phone.startsWith("+") || phone.startsWith("0")) return phone;
  if (phone.startsWith("60")) return `+${phone}`;
  return phone;
};

const RenterDetail = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const qrResult = location.state?.qrResult || null;

  const [data, setData] = useState(null);

  const handleLookup = async (code) => {
    try {
      const resp = await qrLookup(code);
      const record = resp?.record || resp?.data || resp?.result || resp?.customer || resp;

      if (!record) {
        alert(resp?.message || resp?.error || "Record not found");
        navigate("/scanner", { replace: true });
        return;
      }
      setData({ record: normalizeRecord(record), meta: resp });
    } catch (error) {
      console.error("QR lookup error:", error);
      alert(error?.response?.data?.error || "Failed to lookup QR. Please try again.");
      navigate("/scanner", { replace: true });
    }
  };

  useEffect(() => {
    if (qrResult) handleLookup(qrResult);
  }, [qrResult]);

  if (!data) {
    return (
      <MainLayout>
        <Loader />
      </MainLayout>
    );
  }

  const r = data.record;
  const bil = r?.bil;

  const goUpdate = () => {
    if (!bil) return alert("BIL not found");
    navigate(`/sewa-client-update/${bil}`, { state: { record: r } });
  };

  const goRemarks = () => {
    if (!bil) return alert("BIL not found");
    navigate(`/remarks/${bil}`, {
      state: {
        bil,
        kod_kawasan: r?.kodKawasan,
        nombor_unit: r?.nombor_unit,
      },
    });
  };

  return (
    <MainLayout>
      <div className="flex flex-col items-center bg-gray-100 pt-6 pb-4 px-4">
        <h2 className="text-2xl font-semibold text-gray-800">Butiran Pelanggan Sewa</h2>
        <p className="text-gray-500 text-sm mt-1">QR Lookup Result</p>
      </div>

      <div className="max-w-3xl mx-auto px-4 mt-4">
        <div className="bg-white rounded-xl shadow p-3 flex gap-2">
          <button
            onClick={goUpdate}
            className="flex-1 py-2 rounded-lg bg-blue-600 text-white font-semibold hover:bg-blue-700"
          >
            Update
          </button>

          <button
            onClick={goRemarks}
            className="flex-1 py-2 rounded-lg bg-green-600 text-white font-semibold hover:bg-green-700"
          >
            Remarks
          </button>
        </div>
      </div>

      <div className="p-6 bg-white rounded-xl shadow-lg mx-auto max-w-3xl mt-4">
        <table className="w-full text-sm">
          <tbody>
            <Row label="BIL" value={r?.bil} />
            <Row label="Nama Penyewa" value={r?.nama_penyewa || r?.nama || r?.customerName} />
            <Row label="No Kad / NIC" value={r?.no_kad || r?.nomborKad || r?.noNic || r?.no_nic} />
            <Row label="Telefon" value={formatPhone(r?.telefon)} />
            <Row label="Email" value={r?.email} />
            <Row label="No Harta" value={r?.nombor_harta || r?.noHarta} />
            <Row label="No Akaun" value={r?.nombor_akaun || r?.noAkaun || r?.no_akaun} />
            <Row label="No Unit" value={r?.nombor_unit || r?.noUnit} />
            <Row label="Kod Kawasan" value={r?.kodKawasan} />
            <Row label="Kawasan" value={r?.kawasan} />
            <Row label="Zon" value={r?.zon} />
            <Row label="Kategori" value={r?.kategori || r?.category} />
            <Row label="Status" value={r?.status_unit || r?.status} />

            <Row
              label="Caj Sewa"
              value={formatCurrency(r?.caj_sewa)}
              valueClassName="text-blue-700 font-bold"
            />
            <Row
              label="Bayaran"
              value={formatCurrency(r?.bayaran_sewa)}
              valueClassName="text-green-700 font-bold"
            />
            <Row
              label="Tunggakan"
              value={formatCurrency(r?.tunggakan_sewa)}
              valueClassName="text-red-700 font-bold"
            />
          </tbody>
        </table>
      </div>
    </MainLayout>
  );
};

const Row = ({ label, value, valueClassName = "text-gray-800 font-semibold" }) => (
  <tr className="border-b border-gray-100 hover:bg-gray-50 transition">
    <td className="py-2 px-4 font-medium text-gray-600 w-1/3">{label}</td>
    <td className={`py-2 px-4 ${valueClassName}`}>{value || "-"}</td>
  </tr>
);

export default RenterDetail;
